// Genera il PDF di preventivo/proforma. Non dipende da stato React né dal DOM:
// tutti i dati arrivano da GeneraPreventivoInput, così lo stesso codice gira anche lato Node.
// L'unica parte legata al browser è imgUrlToBase64, con la stessa firma di generaProformaPdf.ts.
// Restituisce { blob, filename }.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Tipi condivisi con NuovoPreventivoView
export interface VocePreventivo {
    id: string;
    descrizione: string;
    quantita: number;
    unita: 'ore' | 'km' | 'forfait' | 'n.' | 'gg';
    prezzoUnitario: number;
    scontoPercentuale: number;

    // Dati per le maggiorazioni automatiche, tutti opzionali:
    // data/orari -> notte e festivi, kmDallaSede -> distanza,
    // dataInizioRichiesta -> urgenza, dataScadenzaRelazione -> urgenza relazione.
    dataServizio?: string;          // YYYY-MM-DD
    oraInizio?: string;             // HH:mm
    oraFine?: string;               // HH:mm
    kmDallaSede?: number;
    dataInizioRichiesta?: string;   // YYYY-MM-DDTHH:mm (datetime-local)
    dataScadenzaRelazione?: string; // YYYY-MM-DDTHH:mm
}

export interface MaggiorazioneApplicata { label: string; percentuale: number; }

export interface CalcoloVoce {
    subtotaleBase: number;       // prezzoUnitario * quantita (lordo, senza sconto)
    maggiorazioni: MaggiorazioneApplicata[];
    percentualeTotale: number;   // somma % maggiorazioni
    totale: number;              // subtotaleBase * (1 + percentualeTotale/100) * (1 - sconto%)
}

// Festività italiane fisse + Pasqua/Pasquetta (algoritmo di Gauss)
const isFestivoItaliano = (d: Date): boolean => {
    if (d.getDay() === 0) return true; // domenica
    const m = d.getMonth() + 1;
    const day = d.getDate();
    const fissi = [
        [1, 1], [1, 6], [4, 25], [5, 1], [6, 2],
        [8, 15], [11, 1], [12, 8], [12, 25], [12, 26],
    ];
    if (fissi.some(([fm, fd]) => fm === m && fd === day)) return true;
    // Pasqua (Gauss)
    const Y = d.getFullYear();
    const a = Y % 19, b = Math.floor(Y / 100), c = Y % 100;
    const d2 = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d2 - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4;
    const L = (32 + 2 * e + 2 * i - h - k) % 7;
    const mo = Math.floor((a + 11 * h + 22 * L) / 451);
    const month = Math.floor((h + L - 7 * mo + 114) / 31);
    const dayP = ((h + L - 7 * mo + 114) % 31) + 1;
    const pasqua = new Date(Y, month - 1, dayP);
    const pasquetta = new Date(Y, month - 1, dayP + 1);
    return (d.getMonth() === pasqua.getMonth() && d.getDate() === pasqua.getDate())
        || (d.getMonth() === pasquetta.getMonth() && d.getDate() === pasquetta.getDate());
};

// True se l'orario "HH:mm" cade nella fascia notturna, anche a cavallo della mezzanotte.
const isOrarioNotturno = (ora: string | undefined, inizio?: string, fine?: string): boolean => {
    if (!ora || !inizio || !fine) return false;
    const toMin = (s: string) => {
        const [h, m] = s.split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };
    const o = toMin(ora), i = toMin(inizio), f = toMin(fine);
    if (i === f) return false;
    return i < f ? (o >= i && o < f) : (o >= i || o < f); // wrap mezzanotte (es. 22:00-06:00)
};

/**
 * Calcola subtotale e maggiorazioni di una voce in base ai settings.
 * Notte+Festivo prevale sulle due singole; per distanza e urgenza vale la fascia più alta raggiunta.
 */
export const calcolaVoce = (v: VocePreventivo, settings: any | null): CalcoloVoce => {
    const sconto = (v.scontoPercentuale || 0) / 100;
    const subtotaleBase = (v.prezzoUnitario || 0) * (v.quantita || 0);
    const magg: MaggiorazioneApplicata[] = [];
    if (!settings) {
        return { subtotaleBase, maggiorazioni: [], percentualeTotale: 0, totale: subtotaleBase * (1 - sconto) };
    }

    // Notte / Festivo / Notte+Festivo
    if (v.dataServizio) {
        const dataObj = new Date(v.dataServizio + 'T00:00:00');
        const festivo = isFestivoItaliano(dataObj);
        const notteInizio = isOrarioNotturno(v.oraInizio, settings.orarioNotturnoInizio, settings.orarioNotturnoFine);
        const notteFine = isOrarioNotturno(v.oraFine, settings.orarioNotturnoInizio, settings.orarioNotturnoFine);
        const notte = notteInizio || notteFine;
        if (notte && festivo && settings.majNightHoliday) {
            magg.push({ label: 'Notte/Festivo', percentuale: settings.majNightHoliday });
        } else if (notte && settings.majNight) {
            magg.push({ label: 'Notturno', percentuale: settings.majNight });
        } else if (festivo && settings.majHoliday) {
            magg.push({ label: 'Festivo', percentuale: settings.majHoliday });
        }
    }

    // Distanza dalla sede
    // Fasce dalla più alta alla più bassa: vince la prima che corrisponde.
    if (v.kmDallaSede && v.kmDallaSede > 0) {
        if (v.kmDallaSede > 250 && settings.majDistance250Plus) {
            magg.push({ label: `Distanza oltre 250km`, percentuale: settings.majDistance250Plus });
        } else if (v.kmDallaSede >= 150 && v.kmDallaSede <= 250 && settings.majDistance150250) {
            magg.push({ label: `Distanza 150-250km`, percentuale: settings.majDistance150250 });
        } else if (v.kmDallaSede >= 100 && v.kmDallaSede < 150 && settings.majDistance100150) {
            magg.push({ label: `Distanza 100-150km`, percentuale: settings.majDistance100150 });
        } else if (v.kmDallaSede >= 50 && v.kmDallaSede < 100 && settings.majDistance50100) {
            magg.push({ label: `Distanza 50-100km`, percentuale: settings.majDistance50100 });
        }
    }

    // Urgenza attivazione (quanto manca rispetto ad adesso)
    if (v.dataInizioRichiesta) {
        const ms = new Date(v.dataInizioRichiesta).getTime() - Date.now();
        const ore = ms / 3_600_000;
        if (ore > 0 && ore <= 12 && settings.majUrgency12) {
            magg.push({ label: 'Urgenza ≤12h', percentuale: settings.majUrgency12 });
        } else if (ore > 0 && ore <= 24 && settings.majUrgency24) {
            magg.push({ label: 'Urgenza ≤24h', percentuale: settings.majUrgency24 });
        } else if (ore > 0 && ore <= 48 && settings.majUrgency48) {
            magg.push({ label: 'Urgenza ≤48h', percentuale: settings.majUrgency48 });
        }
    }

    // Urgenza relazione
    if (v.dataScadenzaRelazione) {
        const ms = new Date(v.dataScadenzaRelazione).getTime() - Date.now();
        const ore = ms / 3_600_000;
        if (ore > 0 && ore <= 24 && settings.majReport24) {
            magg.push({ label: 'Relazione ≤24h', percentuale: settings.majReport24 });
        } else if (ore > 0 && ore <= 48 && settings.majReport48) {
            magg.push({ label: 'Relazione ≤48h', percentuale: settings.majReport48 });
        }
    }

    const percentualeTotale = magg.reduce((acc, m) => acc + (m.percentuale || 0), 0);
    const totale = subtotaleBase * (1 + percentualeTotale / 100) * (1 - sconto);
    return { subtotaleBase, maggiorazioni: magg, percentualeTotale, totale };
};

// Anagrafica cliente per l'intestazione
export interface ClientePdfData {
    denominazione: string;
    indirizzo: string;
    citta: string;
    cap: string;
    provincia: string;
    cfPiva: string;
    email: string;
    telefono: string;
}

export interface GeneraPreventivoInput {
    type: 'preventivo' | 'proforma';
    settings: any;                 // globalSettings (agencyName, address, iban, maggiorazioni, header, ...)
    clientePdf: ClientePdfData;
    voci: VocePreventivo[];
    note: string;
    ivaPercentuale: number;
    statoPreventivo: string;       // BOZZA | INVIATO | ACCETTATO | RIFIUTATO
    docNumber?: string;            // numero ufficiale (PF-/PV-YYYY-NNNN), se assente se ne genera uno
    nascondiTotale?: boolean;      // preventivo: nasconde il box totali (mai per proforma)
    nascondiIva?: boolean;         // mostra solo il totale imponibile, senza IVA
}

// Riquadro dell'intestazione, uguale a quello della relazione: 18,5 × 3,3 cm, centrato.
const HEADER_IMG_TOP = 5;
const HEADER_IMG_W = 185;
const HEADER_IMG_H = 33;

// Disegna l'immagine dentro il riquadro senza deformarla: un'immagine nelle proporzioni del
// riquadro (es. 1850×330 px) lo riempie, le altre restano centrate con bordo bianco.
const drawHeaderImage = (doc: jsPDF, b64: string, pageW: number) => {
    let w = HEADER_IMG_W;
    let h = HEADER_IMG_H;
    try {
        const p = doc.getImageProperties(b64);
        if (p.width > 0 && p.height > 0) {
            const ratio = p.width / p.height;
            if (ratio > HEADER_IMG_W / HEADER_IMG_H) h = HEADER_IMG_W / ratio;
            else w = HEADER_IMG_H * ratio;
        }
    } catch { /* proporzioni ignote: riquadro pieno */ }
    const x = (pageW - w) / 2;
    const y = HEADER_IMG_TOP + (HEADER_IMG_H - h) / 2;
    doc.addImage(b64, 'JPEG', x, y, w, h);
};

// Converte un URL immagine in base64 per jsPDF (stessa firma di generaProformaPdf.ts)
const imgUrlToBase64 = (url: string): Promise<string | null> => {
    return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvas.getContext('2d')!.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/jpeg', 0.92));
            } catch { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
};

export const generaPreventivoPdf = async (
    input: GeneraPreventivoInput,
): Promise<{ blob: Blob; filename: string } | null> => {
    const {
        type, settings, clientePdf, voci, note,
        ivaPercentuale, statoPreventivo, docNumber: docNumberOpt, nascondiTotale, nascondiIva,
    } = input;
    // "Nascondi totale" vale solo per il preventivo, la proforma mostra sempre il box.
    const hideTotals = !!nascondiTotale && type !== 'proforma';
    // "Nascondi IVA" vale per entrambi: si mostra solo il totale imponibile.
    const hideIva = !!nascondiIva;

    // Stessa logica di calcolo del componente (calcolaVoce).
    const totali = (() => {
        const imponibile = voci.reduce((acc, voce) => acc + calcolaVoce(voce, settings).totale, 0);
        const iva = imponibile * (ivaPercentuale / 100);
        return { imponibile, iva, totale: imponibile + iva };
    })();

    try {
        // Serve solo l'header: preventivo e proforma non hanno footer.
        const mandatoHeaderUrl = settings?.mandatoHeaderUrl || (settings as any)?.mandatoHeaderImageUrl || '';
        const [headerBase64] = await Promise.all([
            mandatoHeaderUrl ? imgUrlToBase64(mandatoHeaderUrl) : Promise.resolve(null),
        ]);

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const PAGE_W = 210;
        const PAGE_H = 297;
        const MARGIN = 14;
        const CONTENT_W = PAGE_W - MARGIN * 2;

        // Palette colori
        const COLOR_PRIMARY: [number, number, number] = [31, 41, 55];
        const COLOR_ACCENT: [number, number, number]  = [156, 163, 175];
        const COLOR_DARK: [number, number, number]    = [17, 24, 39];
        const COLOR_MID: [number, number, number]     = [107, 114, 128];
        const COLOR_LIGHT: [number, number, number]   = [249, 250, 251];
        const COLOR_WHITE: [number, number, number]   = [255, 255, 255];

        // Dati agenzia dai settings
        const agencyName    = settings?.agencyName    || 'Agenzia Investigativa';
        const agencyAddress = settings?.address       || '';
        const agencyCity    = settings?.city          || '';
        const agencyPhone   = settings?.phone         || '';
        const agencyEmail   = settings?.email         || '';
        const agencyVat     = settings?.vatNumber     || '';
        const agencyWeb     = settings?.website       || '';

        // Dati documento
        const isProforma    = type === 'proforma';
        const titoloDoc     = isProforma ? 'FATTURA PROFORMA' : 'PREVENTIVO';

        const nomeCliente         = clientePdf.denominazione;
        const emailCliente        = clientePdf.email;
        const telefonoCliente     = clientePdf.telefono;
        const indirizzoClientePdf = [clientePdf.indirizzo, clientePdf.cap, clientePdf.citta, clientePdf.provincia].filter(Boolean).join(', ');
        const cfPivaClientePdf    = clientePdf.cfPiva;

        const oggi          = new Date();
        const dataStr       = oggi.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const docNumber     = docNumberOpt
            ?? `${isProforma ? 'PF' : 'PR'}-${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, '0')}${String(oggi.getDate()).padStart(2, '0')}`;

        // Intestazione nello stesso riquadro della relazione (18,5 × 3,3 cm, centrato), così la
        // stessa immagine si vede uguale su relazione, preventivo e proforma. Proporzioni mantenute.
        const HEADER_H = headerBase64 ? HEADER_IMG_TOP + HEADER_IMG_H : 30;
        // Nessun footer: il contenuto arriva fino al margine inferiore.
        const FOOTER_H = 0;

        // Il nome è rimasto drawHeaderFooter per non toccare i call site di didDrawPage.
        const drawHeaderFooter = (_pageNum: number, _totalPages: number) => {
            if (headerBase64) {
                drawHeaderImage(doc, headerBase64, PAGE_W);
            } else {
                // In mancanza dell'immagine: barra colorata con i dati agenzia
                doc.setFillColor(...COLOR_PRIMARY);
                doc.rect(0, 0, PAGE_W, 28, 'F');

                doc.setFillColor(...COLOR_ACCENT);
                doc.rect(0, 28, PAGE_W, 2, 'F');

                doc.setTextColor(...COLOR_WHITE);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(16);
                doc.text(agencyName.toUpperCase(), MARGIN, 11);

                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                const tagRight = `${agencyAddress ? agencyAddress + '  |  ' : ''}${agencyCity}`;
                doc.text(tagRight, PAGE_W - MARGIN, 11, { align: 'right' });

                const contacts = [agencyPhone, agencyEmail, agencyWeb].filter(Boolean).join('   |   ');
                doc.setFontSize(7);
                doc.text(contacts, MARGIN, 20);
                if (agencyVat) {
                    doc.text(`P.IVA: ${agencyVat}`, PAGE_W - MARGIN, 20, { align: 'right' });
                }
            }

            doc.setTextColor(...COLOR_DARK);
        };

        // Corpo pagina 1
        let cursorY = HEADER_H + 10; // spazio sotto l'header

        // Soglia oltre la quale un blocco va a pagina nuova (12mm di margine dal bordo).
        const CONTENT_BOTTOM_LIMIT = PAGE_H - FOOTER_H - 12;

        // Apre una nuova pagina se il blocco non ci sta. Serve per i box che non vanno
        // spezzati (totali, note, bonifico, disclaimer); l'header viene ridisegnato alla fine.
        const ensureSpace = (needed: number) => {
            if (cursorY + needed > CONTENT_BOTTOM_LIMIT) {
                doc.addPage();
                cursorY = HEADER_H + 10;
            }
        };

        // Titolo documento
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.setTextColor(...COLOR_PRIMARY);
        doc.text(titoloDoc, MARGIN, cursorY);

        // Badge dello stato accanto al titolo
        const badgeX = MARGIN + doc.getTextWidth(titoloDoc) + 6;
        const badgeColors: Record<string, [number, number, number]> = {
            BOZZA:    [156, 163, 175],
            INVIATO:  [59, 130, 246],
            ACCETTATO:[34, 197, 94],
            RIFIUTATO:[239, 68, 68],
        };
        const statoBadge = (statoPreventivo || 'BOZZA').toUpperCase();
        const bc = badgeColors[statoBadge] || badgeColors.BOZZA;
        doc.setFillColor(...bc);
        doc.roundedRect(badgeX, cursorY - 5.5, 22, 7, 1.5, 1.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(...COLOR_WHITE);
        doc.text(statoBadge, badgeX + 11, cursorY - 0.5, { align: 'center' });
        doc.setTextColor(...COLOR_DARK);

        // Numero e data, allineati a destra
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...COLOR_MID);
        doc.text(`N° ${docNumber}`, PAGE_W - MARGIN, cursorY - 4, { align: 'right' });
        doc.text(`Data emissione: ${dataStr}`, PAGE_W - MARGIN, cursorY + 1, { align: 'right' });

        cursorY += 8;

        // Linea separatrice
        doc.setDrawColor(...COLOR_ACCENT);
        doc.setLineWidth(0.4);
        doc.line(MARGIN, cursorY, PAGE_W - MARGIN, cursorY);
        cursorY += 8;

        // Box cliente
        const BOX_H = 40;
        doc.setFillColor(...COLOR_LIGHT);
        doc.roundedRect(MARGIN, cursorY, CONTENT_W, BOX_H, 2, 2, 'F');
        doc.setDrawColor(209, 213, 219);
        doc.setLineWidth(0.3);
        doc.roundedRect(MARGIN, cursorY, CONTENT_W, BOX_H, 2, 2, 'S');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...COLOR_MID);
        doc.text('INTESTATARIO', MARGIN + 4, cursorY + 5);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...COLOR_DARK);
        doc.text(nomeCliente || '—', MARGIN + 4, cursorY + 13);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...COLOR_MID);
        if (indirizzoClientePdf) doc.text(indirizzoClientePdf, MARGIN + 4, cursorY + 20);
        const clientContacts = [emailCliente, telefonoCliente].filter(Boolean).join('   ·   ');
        if (clientContacts) doc.text(clientContacts, MARGIN + 4, cursorY + 27);
        if (cfPivaClientePdf) {
            doc.setFontSize(7.5);
            doc.text(`C.F./P.IVA: ${cfPivaClientePdf}`, MARGIN + 4, cursorY + 34);
        }

        // "Valido per 30 giorni" solo sui preventivi: su una proforma sembrerebbe un termine di pagamento.
        if (!isProforma) {
            // Separatore verticale e colonna destra del box
            doc.setDrawColor(209, 213, 219);
            doc.line(CONTENT_W - 40, cursorY + 3, CONTENT_W - 40, cursorY + BOX_H - 3);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(...COLOR_MID);
            doc.text('VALIDO PER', PAGE_W - MARGIN - 4, cursorY + 5, { align: 'right' });
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(...COLOR_PRIMARY);
            doc.text('30 giorni', PAGE_W - MARGIN - 4, cursorY + 13, { align: 'right' });
        }

        cursorY += BOX_H + 10;

        // Tabella voci: per ogni voce la riga principale più una sotto-riga per ogni
        // maggiorazione applicata, così il cliente vede come si arriva al totale.
        const tableBody: any[] = [];
        voci.forEach(v => {
            const calc = calcolaVoce(v, settings);
            const hasSconto = (v.scontoPercentuale || 0) > 0;
            // Totale con maggiorazioni ma prima dello sconto: è il valore mostrato barrato.
            const totaleLordo = calc.subtotaleBase * (1 + calc.percentualeTotale / 100);
            const totaleFinaleStr = `€ ${calc.totale.toFixed(2)}`;
            const totaleLordoStr = `€ ${totaleLordo.toFixed(2)}`;

            // Con lo sconto la cella ha due righe: il lordo (barrato in didDrawCell
            // tramite _strikeFirstLine) e sotto il finale.
            const totaleCell: any = hasSconto
                ? { content: `${totaleLordoStr}\n${totaleFinaleStr}`, _strikeFirstLine: true }
                : totaleFinaleStr;

            tableBody.push([
                v.descrizione,
                `${v.quantita}`,
                v.unita,
                `€ ${v.prezzoUnitario.toFixed(2)}`,
                hasSconto ? `${v.scontoPercentuale}%` : '—',
                totaleCell,
            ]);
            calc.maggiorazioni.forEach(m => {
                const importoMagg = calc.subtotaleBase * (m.percentuale / 100);
                // L'Helvetica di jsPDF usa WinAnsi: caratteri come ↳ e ≤ uscirebbero come
                // quadratini, quindi li sostituisco solo in stampa.
                const labelPdf = m.label.replace(/≤/g, '<=').replace(/≥/g, '>=');
                tableBody.push([
                    {
                        content: `» Maggiorazione ${labelPdf}`,
                        colSpan: 4,
                        styles: { fontStyle: 'italic', textColor: COLOR_MID, fillColor: [249, 250, 251] as [number, number, number] }
                    },
                    {
                        content: `+${m.percentuale}%`,
                        styles: { halign: 'center', fontStyle: 'italic', textColor: COLOR_MID, fillColor: [249, 250, 251] as [number, number, number] }
                    },
                    {
                        content: `€ ${importoMagg.toFixed(2)}`,
                        styles: { halign: 'right', fontStyle: 'italic', textColor: COLOR_MID, fillColor: [249, 250, 251] as [number, number, number] }
                    }
                ]);
            });
        });

        autoTable(doc, {
            startY: cursorY,
            // 12mm di margine sotto la tabella, come per il resto della pagina.
            margin: { left: MARGIN, right: MARGIN, bottom: FOOTER_H + 12 },
            head: [['Descrizione', 'Q.tà', 'Unità', 'P. Unit.', 'Sconto', 'Totale']],
            body: tableBody,
            styles: {
                fontSize: 9,
                cellPadding: { top: 2.2, bottom: 2.2, left: 4, right: 4 },
                textColor: [17, 24, 39],
                lineColor: [229, 231, 235],
                lineWidth: 0.2,
            },
            headStyles: {
                fillColor: COLOR_PRIMARY,
                textColor: COLOR_WHITE,
                fontStyle: 'bold',
                fontSize: 8,
                cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
            },
            alternateRowStyles: {
                fillColor: [249, 250, 251],
            },
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 18, halign: 'center' },
                2: { cellWidth: 18, halign: 'center' },
                3: { cellWidth: 26, halign: 'right' },
                4: { cellWidth: 18, halign: 'center', textColor: [220, 38, 38] },
                5: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
            },
            didParseCell: (data) => {
                // Evidenzia l'ultima colonna (totale)
                if (data.column.index === 5 && data.section === 'body') {
                    data.cell.styles.textColor = COLOR_PRIMARY;
                }
            },
            // Celle totale con sconto: barra la prima riga (lordo) e lascia leggibile il finale.
            didDrawCell: (data) => {
                if (data.column.index !== 5 || data.section !== 'body') return;
                const raw: any = data.cell.raw;
                if (!raw || typeof raw !== 'object' || !raw._strikeFirstLine) return;
                const firstLine: string = String(raw.content).split('\n')[0];
                // Misura il testo nel font della cella (helvetica bold 9pt).
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(9);
                // getStringUnitWidth è in unità di font: si converte in mm con lo scaleFactor del documento.
                const fontSize = 9;
                const scaleFactor = (doc.internal as any).scaleFactor || 2.8346456692913385;
                const uw = (doc as any).getStringUnitWidth(firstLine) as number;
                const tw = uw * fontSize / scaleFactor;
                const padTop = 2.2;
                const padRight = 4;
                const lineH = 4;            // ~9pt ≈ 3.17mm, lineHeight ≈ 4mm
                const strikeY = data.cell.y + padTop + lineH * 0.5;
                const xRight = data.cell.x + data.cell.width - padRight;
                const xLeft = xRight - tw;
                doc.setDrawColor(120, 120, 120);
                doc.setLineWidth(0.4);
                doc.line(xLeft - 0.5, strikeY, xRight + 0.5, strikeY);
            },
            // Ridisegna l'header sulle pagine aggiunte dalla tabella
            didDrawPage: (data) => {
                drawHeaderFooter(data.pageNumber, 1); // totalPages si aggiorna alla fine
            },
        });

        cursorY = (doc as any).lastAutoTable.finalY + 10;

        // Box totali, nascosto sui preventivi con "nascondi totale" (la proforma lo mostra sempre).
        if (!hideTotals && hideIva) {
        // "Nascondi IVA": una sola barra TOTALE con l'imponibile.
        ensureSpace(20);
        const TOTALS_W = 82;
        const TOTALS_X = PAGE_W - MARGIN - TOTALS_W;
        doc.setFillColor(...COLOR_PRIMARY);
        doc.roundedRect(TOTALS_X, cursorY, TOTALS_W, 15, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...COLOR_WHITE);
        doc.text('TOTALE', TOTALS_X + 4, cursorY + 10);
        doc.setFontSize(13);
        doc.text(`€ ${totali.imponibile.toFixed(2)}`, TOTALS_X + TOTALS_W - 4, cursorY + 10, { align: 'right' });
        cursorY += 24;
        } else if (!hideTotals) {
        // Altezza fissa: 36mm box + 15mm barra TOTALE + ~5mm di spazio.
        ensureSpace(45);
        const TOTALS_W = 82;
        const TOTALS_X = PAGE_W - MARGIN - TOTALS_W;

        // Sfondo
        doc.setFillColor(...COLOR_LIGHT);
        doc.roundedRect(TOTALS_X, cursorY, TOTALS_W, 36, 2, 2, 'F');
        doc.setDrawColor(209, 213, 219);
        doc.setLineWidth(0.3);
        doc.roundedRect(TOTALS_X, cursorY, TOTALS_W, 36, 2, 2, 'S');

        // Imponibile
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...COLOR_MID);
        doc.text('Imponibile', TOTALS_X + 4, cursorY + 8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLOR_DARK);
        doc.text(`€ ${totali.imponibile.toFixed(2)}`, TOTALS_X + TOTALS_W - 4, cursorY + 8, { align: 'right' });

        // IVA
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLOR_MID);
        doc.text(`IVA ${ivaPercentuale}%`, TOTALS_X + 4, cursorY + 16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLOR_DARK);
        doc.text(`€ ${totali.iva.toFixed(2)}`, TOTALS_X + TOTALS_W - 4, cursorY + 16, { align: 'right' });

        // Divisore
        doc.setDrawColor(...COLOR_ACCENT);
        doc.setLineWidth(0.5);
        doc.line(TOTALS_X + 4, cursorY + 20, TOTALS_X + TOTALS_W - 4, cursorY + 20);

        // TOTALE
        doc.setFillColor(...COLOR_PRIMARY);
        doc.roundedRect(TOTALS_X, cursorY + 21, TOTALS_W, 15, 0, 0, 'F');
        doc.roundedRect(TOTALS_X, cursorY + 21, TOTALS_W, 15, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...COLOR_WHITE);
        doc.text('TOTALE', TOTALS_X + 4, cursorY + 31);
        doc.setFontSize(13);
        doc.text(`€ ${totali.totale.toFixed(2)}`, TOTALS_X + TOTALS_W - 4, cursorY + 31, { align: 'right' });

        cursorY += 45;
        }

        // Note
        if (note && note.trim()) {
            // Altezza calcolata prima, per gestire il cambio pagina.
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            const splitNote = doc.splitTextToSize(note, CONTENT_W - 8);
            const noteH = 10 + splitNote.length * 5;
            ensureSpace(noteH + 8);

            doc.setDrawColor(209, 213, 219);
            doc.setFillColor(...COLOR_LIGHT);
            doc.setLineWidth(0.3);
            doc.roundedRect(MARGIN, cursorY, CONTENT_W, noteH, 2, 2, 'FD');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(...COLOR_MID);
            doc.text('NOTE', MARGIN + 4, cursorY + 6);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_DARK);
            doc.text(splitNote, MARGIN + 4, cursorY + 13);

            cursorY += noteH + 8;
        }

        // Modalità di pagamento (solo proforma): dicitura fiscale standard
        // più il blocco bonifico con IBAN e intestatario dai settings.
        if (isProforma) {
            const iban         = (settings as any)?.iban         || '__________________________';
            const intestatario = (settings as any)?.intestatario || '______';
            const bancaNome    = (settings as any)?.bancaNome    || '';

            // Dicitura fiscale
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(8);
            doc.setTextColor(...COLOR_MID);
            const testoStandard =
                'Documento non valido ai fini fiscali e IVA. La presente proforma viene emessa ' +
                'esclusivamente quale richiesta di pagamento/riepilogo degli importi dovuti. ' +
                'A seguito dell\'avvenuto pagamento sarà emessa regolare fattura elettronica ' +
                'nei termini di legge.';
            const splitTesto = doc.splitTextToSize(testoStandard, CONTENT_W - 8);
            const testoH = 6 + splitTesto.length * 4 + 4;
            ensureSpace(testoH + 6);
            doc.setDrawColor(209, 213, 219);
            doc.setFillColor(...COLOR_LIGHT);
            doc.setLineWidth(0.3);
            doc.roundedRect(MARGIN, cursorY, CONTENT_W, testoH, 2, 2, 'FD');
            doc.text(splitTesto, MARGIN + 4, cursorY + 6);
            cursorY += testoH + 6;

            // Bonifico: altezza calcolata prima, così il box resta su una sola pagina.
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            const linePayLines = [
                'Si invita a voler effettuare il pagamento mediante bonifico bancario alle seguenti coordinate:',
                `Intestatario: ${intestatario}`,
                bancaNome ? `Banca: ${bancaNome}` : null,
                `IBAN: ${iban}`,
                `Causale: pagamento proforma ${docNumber}`,
            ].filter(Boolean) as string[];
            const splitPay = linePayLines.flatMap(l => doc.splitTextToSize(l, CONTENT_W - 8));
            const payH = 10 + splitPay.length * 4 + 4;
            ensureSpace(payH + 8);

            doc.setFillColor(...COLOR_LIGHT);
            doc.setDrawColor(209, 213, 219);
            doc.setLineWidth(0.3);
            doc.roundedRect(MARGIN, cursorY, CONTENT_W, payH, 2, 2, 'FD');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_DARK);
            doc.text('MODALITÀ DI PAGAMENTO', MARGIN + 4, cursorY + 6);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(...COLOR_MID);
            doc.text(splitPay, MARGIN + 4, cursorY + 12);

            cursorY += payH + 8;
        }

        // Dichiarazione finale: solo sui preventivi (validità 30 giorni).
        if (!isProforma) {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(7.5);
            doc.setTextColor(...COLOR_MID);
            const disclaimer = `Il presente ${titoloDoc.toLowerCase()} è valido per 30 giorni dalla data di emissione ed è soggetto a conferma scritta.`;
            const splitDisclaimer = doc.splitTextToSize(disclaimer, CONTENT_W);
            ensureSpace(splitDisclaimer.length * 3.5 + 4);
            doc.text(splitDisclaimer, MARGIN, cursorY);
        }

        // Aggiorna l'header con il numero di pagine definitivo
        const totalPages = (doc as any).internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            drawHeaderFooter(i, totalPages);
        }

        // Blob + filename
        const safeName = nomeCliente.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_\-]/g, '');
        const filename = `${titoloDoc}_${safeName}_${oggi.getFullYear()}${String(oggi.getMonth()+1).padStart(2,'0')}${String(oggi.getDate()).padStart(2,'0')}.pdf`;

        const blob = doc.output('blob') as Blob;
        return { blob, filename };
    } catch (e) {
        console.error('[generaPreventivoPdf] errore:', e);
        return null;
    }
};
